import { useCallback, useEffect, useState } from 'react';
import { getPwaSessionCount } from './sessionTracker.js';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const DISMISSED_KEY = 'orborix.speak.pwa.installDismissedAt';
const INSTALLED_KEY = 'orborix.speak.pwa.installed';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const MIN_SESSIONS = 3;

export type PromptOutcome = 'accepted' | 'dismissed' | 'unavailable';

export interface InstallPromptState {
  /** Banner debería mostrarse (gating ya aplicado). */
  shouldShow: boolean;
  /** API beforeinstallprompt disponible (no aplica iOS Safari). */
  canPrompt: boolean;
  /** iOS Safari → mostrar tutorial manual en vez del prompt nativo. */
  isIosSafari: boolean;
  prompt: () => Promise<PromptOutcome>;
  dismiss: () => void;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari legacy.
  return Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone === true);
}

function detectIosSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) && !('MSStream' in window);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIos && isSafari;
}

function isDismissedRecently(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
}

export function useBeforeInstallPrompt(): InstallPromptState {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(() => isStandalone());
  const [dismissed, setDismissed] = useState<boolean>(() => isDismissedRecently());
  const isIosSafari = detectIosSafari();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onBeforeInstall = (e: Event): void => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = (): void => {
      try {
        window.localStorage.setItem(INSTALLED_KEY, '1');
      } catch {
        // ignore
      }
      setInstalled(true);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const prompt = useCallback(async (): Promise<PromptOutcome> => {
    if (!deferred) return 'unavailable';
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      if (choice.outcome === 'dismissed') {
        try {
          window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
        } catch {
          // ignore
        }
        setDismissed(true);
      }
      return choice.outcome;
    } catch {
      return 'unavailable';
    }
  }, [deferred]);

  const dismiss = useCallback((): void => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    setDismissed(true);
  }, []);

  const sessions = getPwaSessionCount();
  const canPrompt = !!deferred;
  const shouldShow = !installed && !dismissed && sessions >= MIN_SESSIONS && (canPrompt || isIosSafari);

  return { shouldShow, canPrompt, isIosSafari, prompt, dismiss };
}
